import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  CheckCircle,
  Download,
  Loader2,
  FileText,
  Clock,
  Paperclip,
  ChevronDown,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useToast } from 'src/hooks/use-toast';
import { useBillingInfo } from 'src/services/api/queries/billingQueries';
import { useVerifyPurchaseOrder } from 'src/services/api/mutations/billingMutations';
import { Alert } from 'src/components/common/Alert';
import { PDFDownloadLink } from '@react-pdf/renderer';
import BillingPDF from 'src/components/BillingPDF/BillingPDF';
import { BILLING_STATUS_VALUES } from '../../../utils/statusConstants';
import { purchaseOrderStatusColor } from '../../../utils/statusColors';

const transformApiResponse = (apiData) => {
  const { header, candidates, meta, requirements } = apiData;
  const orderDetails = apiData.order_details;

  return {
    id: meta.id,
    poNumber: header.po_number,
    date: header.date,
    client: {
      name: header.customer.name || 'N/A',
      gstin: header.customer.gstin || 'N/A',
      address: {
        line1: header.customer.address_line_1 || '',
        street: header.customer.street || '',
        city: header.customer.city || '',
        state: header.customer.state || '',
        zipCode: header.customer.zip_code || '',
      },
    },
    ahedge: {
      name: header.ahedge.name,
      address: header.ahedge.address,
      cityStatePin: header.ahedge.city_state_pin,
      gstin: header.ahedge.gstin,
    },
    orderDetails: {
      description: orderDetails.description,
      positions: orderDetails.positions,
      totalCTC: orderDetails.total_ctc,
      totalFee: orderDetails.total_fee,
      gstPercentage: orderDetails.gst_percentage,
      gstAmount: orderDetails.gst_amount,
      grandTotal: orderDetails.grand_total,
    },
    candidates: candidates.map((candidate) => ({
      name: candidate.name,
      position: candidate.position,
      ctc: candidate.ctc,
      fee: candidate.fee,
    })),
    requirements: requirements
      ? requirements.map((req) => ({
          title: req.title,
          headcount: req.headcount,
          estimatedCtcPerProfile: req.estimated_ctc_per_profile,
          estimatedTotalCtc: req.estimated_total_ctc,
          estimatedFee: req.estimated_fee,
          requirementId: req.requirement_id,
        }))
      : [],
    meta: {
      status: meta.status,
      customerCategory: meta.customer_category,
      createdAt: meta.created_at,
      currency: meta.currency || 'INR',
    },
  };
};

const formatPoDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const PO_STATUS_ICONS = {
  PENDING_APPROVAL: Clock,
  APPROVED: CheckCircle,
};

const StatusBadge = ({ status }) => {
  const Icon = PO_STATUS_ICONS[status] || FileText;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10.5px] font-semibold uppercase tracking-[0.04em] ${purchaseOrderStatusColor(
        status
      )}`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
      {BILLING_STATUS_VALUES[status] || status}
    </span>
  );
};

const BillingInfo = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [showApproveAlert, setShowApproveAlert] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);

  const {
    data: billingResponse,
    isLoading,
    isError,
    error,
  } = useBillingInfo(id);
  const verifyPO = useVerifyPurchaseOrder();
  const isApproving = verifyPO.isPending;

  // The verify mutation invalidates ['billing', 'info', id], so the query
  // refetches on approval and the status reflects automatically.
  const poData = billingResponse?.success
    ? transformApiResponse(billingResponse.data)
    : null;

  const pdfData = poData
    ? {
        poNumber: poData.poNumber,
        date: poData.date,
        from: poData.ahedge,
        to: poData.client,
        orderDetails: poData.orderDetails,
        requirements: poData.requirements,
        candidates: poData.candidates,
        status: poData.meta.status,
        currency: poData.meta.currency,
      }
    : null;

  const handleApprove = () => {
    verifyPO.mutate(id, {
      onSuccess: (response) => {
        if (response?.success) {
          toast({
            title: 'Success',
            description: 'Work order has been approved successfully',
          });
          setShowApproveAlert(false);
        }
      },
      onError: (err) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to approve work order',
          variant: 'destructive',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted">
        <div className="text-center text-red-500 p-6 border border-destructive/30 rounded-xl bg-card shadow-sm max-w-sm">
          <h2 className="text-base font-semibold mb-2">Error Loading Info</h2>
          <p className="text-sm text-red-400">
            {error?.message || 'Failed to load Work Order information'}
          </p>
          <Button
            className="mt-4"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!poData) return <div>No Work Order data found.</div>;

  const currencySymbol = poData.meta.currency === 'INR' ? '₹' : '$';
  const isPendingApproval = poData.meta.status === 'PENDING_APPROVAL';

  return (
    <div className="text-left bg-muted min-h-screen">
      <PageHeader
        breadcrumbs={[
          { label: 'Finance' },
          { label: 'Billing', href: '/purchase-order' },
          { label: poData.poNumber },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {pdfData && (
              <PDFDownloadLink
                document={<BillingPDF data={pdfData} />}
                fileName={`WorkOrder_${poData.poNumber}.pdf`}
                className="no-underline"
              >
                {({ loading }) => (
                  <Button
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Download PDF
                      </>
                    )}
                  </Button>
                )}
              </PDFDownloadLink>
            )}
            {isPendingApproval && (
              <Button
                size="sm"
                className="bg-[#0D9668] hover:bg-[#0B8259] text-white text-xs shadow-[0_2px_6px_rgba(13,150,104,.25)]"
                onClick={() => setShowApproveAlert(true)}
              >
                <CheckCircle className="h-4 w-4" />
                Approve Work Order
              </Button>
            )}
          </div>
        }
      />

      <div className="px-9 pt-8 pb-20 max-w-[820px] mx-auto w-full">
        {/* ── Document card ── */}
        <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
          {/* Blue header stripe */}
          <div className="bg-gradient-to-r from-[#1B6AAF] to-[#155A94] px-7 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/[0.15] flex items-center justify-center flex-shrink-0">
                <FileText
                  className="w-[18px] h-[18px] text-white"
                  strokeWidth={1.8}
                />
              </div>
              <div>
                <div className="text-base font-bold text-white tracking-[-0.02em]">
                  Work Order
                </div>
                <div className="text-[11.5px] text-white/60 font-[450] mt-px">
                  {poData.poNumber}
                </div>
              </div>
            </div>
          </div>

          {/* From / To */}
          <div className="grid grid-cols-2 border-b border-border">
            <div className="px-7 py-[22px]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">
                From
              </div>
              <div className="text-[14px] font-[650] text-foreground-strong tracking-[-0.01em] mb-1.5">
                {poData.ahedge.name}
              </div>
              <div className="text-[12.5px] text-muted-foreground font-[450] leading-[1.65]">
                {poData.ahedge.address}
              </div>
              <div className="text-[12.5px] text-muted-foreground font-[450] leading-[1.65]">
                {poData.ahedge.cityStatePin}
              </div>
              <div className="mt-2 text-[11px] font-[550] font-mono text-muted-foreground tracking-[0.03em]">
                GSTIN: {poData.ahedge.gstin}
              </div>
            </div>
            <div className="px-7 py-[22px] border-l border-border">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">
                To
              </div>
              <div className="text-[14px] font-[650] text-foreground-strong tracking-[-0.01em] mb-1.5">
                {poData.client.name}
              </div>
              {poData.client.address?.line1 && (
                <div className="text-[12.5px] text-muted-foreground font-[450] leading-[1.65]">
                  {poData.client.address.line1}
                </div>
              )}
              {poData.client.address?.street && (
                <div className="text-[12.5px] text-muted-foreground font-[450] leading-[1.65]">
                  {poData.client.address.street}
                </div>
              )}
              {poData.client.address?.city && poData.client.address?.state && (
                <div className="text-[12.5px] text-muted-foreground font-[450] leading-[1.65]">
                  {poData.client.address.city}, {poData.client.address.state}{' '}
                  {poData.client.address.zipCode}
                </div>
              )}
              {poData.client.gstin && poData.client.gstin !== 'N/A' && (
                <div className="mt-2 text-[11px] font-[550] font-mono text-muted-foreground tracking-[0.03em]">
                  GSTIN: {poData.client.gstin}
                </div>
              )}
            </div>
          </div>

          {/* Date + Status meta row */}
          <div className="flex border-b border-border bg-muted">
            <div className="flex-1 px-7 py-3.5 flex items-center gap-2.5">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Date
              </span>
              <span className="text-[13px] font-semibold text-foreground-strong">
                {formatPoDate(poData.date)}
              </span>
            </div>
            <div className="flex-1 px-7 py-3.5 flex items-center gap-2.5 border-l border-border">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Status
              </span>
              <StatusBadge status={poData.meta.status} />
            </div>
          </div>

          {/* Order Summary section label */}
          <div className="px-7 pt-[18px] pb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            Order Summary
          </div>

          {/* Order table */}
          <table className="w-full border-collapse">
            <colgroup>
              <col style={{ width: '46%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
            </colgroup>
            <thead>
              <tr className="bg-muted border-y border-border">
                <th className="px-7 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-left">
                  Description
                </th>
                <th className="px-7 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-center">
                  Nos
                </th>
                <th className="px-7 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                  Unit Price
                </th>
                <th className="px-7 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-7 py-3.5 text-[13px] text-foreground-strong font-medium border-b border-border align-top">
                  {poData.orderDetails.description}
                </td>
                <td className="px-7 py-3.5 text-[13px] text-foreground-strong font-[550] text-center border-b border-border tabular-nums align-top">
                  {poData.orderDetails.positions}
                </td>
                <td className="px-7 py-3.5 text-[13px] text-foreground-strong font-semibold text-right border-b border-border tabular-nums align-top">
                  {currencySymbol}
                  {(
                    poData.orderDetails.totalFee / poData.orderDetails.positions
                  ).toLocaleString()}
                </td>
                <td className="px-7 py-3.5 text-[13px] text-foreground-strong font-semibold text-right border-b border-border tabular-nums align-top">
                  {currencySymbol}
                  {poData.orderDetails.totalFee?.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div>
            <div className="flex items-center justify-end px-7 border-b border-border">
              <div className="w-40 py-[11px] text-right text-[12.5px] font-medium text-muted-foreground pr-4">
                Subtotal
              </div>
              <div className="w-36 py-[11px] text-right text-[13px] font-semibold text-foreground-strong tabular-nums">
                {currencySymbol}
                {poData.orderDetails.totalFee?.toLocaleString()}
              </div>
            </div>
            <div className="flex items-center justify-end px-7 border-b border-border">
              <div className="w-40 py-[11px] text-right text-[12.5px] font-medium text-muted-foreground pr-4">
                GST ({poData.orderDetails.gstPercentage}%)
              </div>
              <div className="w-36 py-[11px] text-right text-[13px] font-semibold text-foreground-strong tabular-nums">
                {currencySymbol}
                {poData.orderDetails.gstAmount?.toLocaleString()}
              </div>
            </div>
            <div className="flex items-center justify-end px-7 bg-[#F4F8FC] dark:bg-muted">
              <div className="w-40 py-[11px] text-right text-[13px] font-[650] text-foreground-strong pr-4">
                Grand Total
              </div>
              <div className="w-36 py-[11px] text-right text-[15px] font-bold text-primary tracking-[-0.01em] tabular-nums">
                {currencySymbol}
                {poData.orderDetails.grandTotal?.toLocaleString()}
              </div>
            </div>
          </div>

          {/* System note */}
          <div className="px-7 py-3.5 text-center text-[11.5px] text-muted-foreground font-[450] italic border-t border-border bg-muted">
            This is a system-generated Work Order. No signature required.
          </div>
        </div>

        {/* ── Terms & Conditions ── */}
        <div className="mt-4 bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
          <button
            type="button"
            className="w-full px-5 py-[15px] flex items-center gap-2.5 hover:bg-muted transition-colors text-left"
            onClick={() => setTermsOpen(!termsOpen)}
          >
            <FileText
              className="w-[18px] h-[18px] text-muted-foreground flex-shrink-0"
              strokeWidth={1.6}
            />
            <span className="text-[14px] font-[620] text-foreground-strong tracking-[-0.01em] flex-1">
              Terms &amp; Conditions
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                termsOpen ? '' : '-rotate-90'
              }`}
              strokeWidth={2}
            />
          </button>
          {termsOpen && (
            <div className="px-5 pb-5 border-t border-border">
              {[
                'GST as applicable. Please reference the PO number on all invoices.',
                'Payment via NEFT/RTGS within 7 days of invoice receipt unless otherwise agreed.',
                'For queries, contact billing@ahedge.in',
              ].map((term) => (
                <div
                  key={term}
                  className="relative pl-4 py-1 text-[12.5px] text-muted-foreground font-[450] leading-[1.7]"
                >
                  <span className="absolute left-0 top-[11px] w-1 h-1 rounded-full bg-border" />
                  {term}
                </div>
              ))}
              <div className="mt-3.5 pt-3.5 border-t border-border flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#ECFDF5] dark:bg-[#0D2E22] flex items-center justify-center flex-shrink-0">
                  <CheckCircle
                    className="w-4 h-4 text-[#0D9668] dark:text-[#34D399]"
                    strokeWidth={2}
                  />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-foreground-strong">
                    Ahedge Procurement
                  </div>
                  <div className="text-[11px] text-muted-foreground font-[450] mt-px">
                    Digitally approved — no signature required
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Annexure ── */}
        {poData.requirements && poData.requirements.length > 0 && (
          <div className="mt-4 bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
            <div className="px-5 py-[15px] flex items-center gap-2.5 border-b border-border">
              <Paperclip
                className="w-[18px] h-[18px] text-muted-foreground flex-shrink-0"
                strokeWidth={1.6}
              />
              <span className="text-[14px] font-[620] text-foreground-strong tracking-[-0.01em]">
                Annexure
              </span>
            </div>
            <table className="w-full border-collapse">
              <colgroup>
                <col style={{ width: '34%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr className="bg-muted border-b border-border">
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-left">
                    Job Title
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-center">
                    Headcount
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                    Est. CTC/Profile
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                    Est. Total CTC
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                    Est. Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {poData.requirements.map((req, index) => (
                  <tr
                    key={`req-${req.requirementId}`}
                    className={
                      index < poData.requirements.length - 1
                        ? 'border-b border-border'
                        : ''
                    }
                  >
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-[550]">
                      {req.title}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-[550] text-center tabular-nums">
                      {req.headcount}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-semibold text-right tabular-nums">
                      {currencySymbol}
                      {req.estimatedCtcPerProfile?.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-semibold text-right tabular-nums">
                      {currencySymbol}
                      {req.estimatedTotalCtc?.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-semibold text-right tabular-nums">
                      {currencySymbol}
                      {req.estimatedFee?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Candidates (if any) */}
        {poData.candidates && poData.candidates.length > 0 && (
          <div className="mt-4 bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
            <div className="px-5 py-[15px] border-b border-border">
              <span className="text-[14px] font-[620] text-foreground-strong tracking-[-0.01em]">
                Candidate Details
              </span>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted border-b border-border">
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-left">
                    Name
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-left">
                    Position
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                    CTC
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {poData.candidates.map((candidate, index) => (
                  <tr
                    key={`candidate-${candidate.name}-${candidate.position}`}
                    className={
                      index < poData.candidates.length - 1
                        ? 'border-b border-border'
                        : ''
                    }
                  >
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-medium">
                      {candidate.name}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-muted-foreground">
                      {candidate.position}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-semibold text-right tabular-nums">
                      {currencySymbol}
                      {candidate.ctc?.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-foreground-strong font-semibold text-right tabular-nums">
                      {currencySymbol}
                      {candidate.fee?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approval confirmation dialog */}
      <Alert
        isOpen={showApproveAlert}
        onClose={() => setShowApproveAlert(false)}
        title="Approve Work Order"
        description={`Are you sure you want to approve work order ${poData.poNumber} for ${currencySymbol}${poData.orderDetails.grandTotal?.toLocaleString()} (incl. GST)?`}
        cancelText="Cancel"
        continueText={isApproving ? 'Processing...' : 'Confirm Approval'}
        onCancel={() => setShowApproveAlert(false)}
        onContinue={handleApprove}
      />
    </div>
  );
};

export default BillingInfo;
